import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { formatUSPhone } from "../lib/format";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ClipboardList,
  Plus,
  FileDown,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  RotateCcw,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { SendMenu } from "../components/SendMenu";
import {
  HOME_TYPES,
  ROOMS,
  CLEANING_TYPES,
  FREQUENCIES,
  PREFERRED_DAYS,
  PREFERRED_TIMES,
  ADDITIONAL_SERVICES,
  STATUS_INFO,
  exportEstimatePDF,
} from "../lib/estimateForm";
import {
  CheckBox,
  ChoiceGrid,
  Section,
  YesNoGroup,
  EMPTY_ESTIMATE as EMPTY,
} from "../components/estimateWidgets";
import { titleCase, lowercaseAll } from "../lib/textCase";
import { useI18n } from "../i18n/I18nContext";
import { logError } from "../lib/logger";

/**
 * Parser best-effort de endereço US livre → {street, city, state, zip}.
 * Suporta:
 *   "123 Main St, Boston, MA 02118"   (com vírgulas — case ideal)
 *   "123 Main St Boston MA 02118"     (sem vírgulas)
 *   "123 Main St, Boston MA"          (sem ZIP)
 *   "123 Main St"                     (apenas rua)
 * Heurística: extrai ZIP do final, depois state code (2 letras maiúsculas),
 * depois assume que o que resta antes do state é "street + cidade" e
 * pega a última palavra/sequência como cidade.
 */
export function parseUSAddress(raw) {
  if (!raw) return { street: "", city: "", state: "", zip: "" };
  let s = String(raw).trim();
  let zip = "";
  let state = "";
  // ZIP no final (5 ou 5-4 dígitos)
  const zipMatch = s.match(/\b(\d{5}(?:-\d{4})?)\s*$/);
  if (zipMatch) {
    zip = zipMatch[1];
    s = s.slice(0, zipMatch.index).trim().replace(/[,\s]+$/, "");
  }
  // State code (2 letras maiúsculas) no final
  const stateMatch = s.match(/(?:^|[\s,])([A-Z]{2})\s*$/);
  if (stateMatch) {
    state = stateMatch[1];
    s = s.slice(0, stateMatch.index + (stateMatch[0].startsWith(",") || stateMatch[0].startsWith(" ") ? 0 : 0)).trim().replace(/[,\s]+$/, "");
    // Robust: remove the trailing state token
    s = s.replace(new RegExp(`\\s*,?\\s*${state}\\s*$`), "").trim();
  }
  // Se temos vírgulas, último segmento é cidade
  let street = s;
  let city = "";
  if (s.includes(",")) {
    const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      city = parts[parts.length - 1];
      street = parts.slice(0, -1).join(", ");
    }
  } else if (state || zip) {
    // Sem vírgulas: heurística para separar cidade do final da string.
    // Sufixos comuns de rua (St, Ave, Rd, etc.) NUNCA são parte da cidade.
    const STREET_SUFFIXES = new Set([
      "st", "street", "ave", "avenue", "rd", "road", "blvd", "boulevard",
      "dr", "drive", "ln", "lane", "way", "ct", "court", "pl", "place",
      "hwy", "highway", "ter", "terrace", "pkwy", "parkway", "cir", "circle",
      "sq", "square", "trl", "trail", "loop",
    ]);
    const tokens = s.split(/\s+/).filter(Boolean);
    if (tokens.length >= 3) {
      // tenta últimas 2 palavras como cidade composta ("New York", "Los Angeles")
      // mas se penúltima for sufixo de rua, força cidade = última palavra apenas
      const penult = tokens[tokens.length - 2].toLowerCase().replace(/\.$/, "");
      if (STREET_SUFFIXES.has(penult)) {
        city = tokens[tokens.length - 1];
        street = tokens.slice(0, -1).join(" ");
      } else if (
        /^[A-Z]/.test(tokens[tokens.length - 2]) &&
        /^[A-Z]/.test(tokens[tokens.length - 1])
      ) {
        city = tokens.slice(-2).join(" ");
        street = tokens.slice(0, -2).join(" ");
      } else {
        city = tokens[tokens.length - 1];
        street = tokens.slice(0, -1).join(" ");
      }
    } else if (tokens.length === 2) {
      city = tokens[1];
      street = tokens[0];
    }
  }
  return {
    street: street.trim(),
    city: city.trim(),
    state,
    zip,
  };
}

const FREQUENCY_TO_CLIENT = {
  weekly: "Semanal",
  bi_monthly: "Quinzenal",
  monthly: "Mensal",
  twice_a_month: "Quinzenal",
  one_time: "Avulsa",
  seasonally: "Mensal",
  annually: "Mensal",
};

const ROOM_TO_CHECKLIST_PT = {
  kitchen: "Cozinha",
  family_room: "Sala de família",
  living_room: "Sala de estar",
  dining_room: "Sala de jantar",
  stairways: "Escadas",
  attic: "Sótão",
  basement: "Porão",
  garage: "Garagem",
  utility_room: "Lavanderia",
  office_den: "Escritório",
  rec_room: "Sala de recreação",
  hallway: "Corredor",
  other: "Outros cômodos",
};

const ADDITIONAL_TO_CHECKLIST_PT = {
  oven_cleaning: "Limpar forno",
  refrigerator: "Limpar geladeira",
  wall_washing: "Lavar paredes",
  mini_blinds: "Limpar persianas",
  light_fixtures: "Limpar luminárias",
  floor_waxing: "Encerar piso",
};

/**
 * Mapeia um Estimate para o payload usado no cadastro de Cliente.
 * Os campos do estimate que não têm correspondente direto vão para o
 * "observations" como bloco resumido, e os cômodos viram cleaning_checklist.
 */
function mapEstimateToClient(est) {
  const parsed = parseUSAddress(est.address || "");
  const address = parsed.street;
  const city = parsed.city;
  const stateCode = parsed.state;
  const zip = parsed.zip;
  // Safety-net: dados via API podem não ter sido normalizados via UI
  const safeName = titleCase(est.name || "");

  // Frequência: primeira selecionada com mapeamento conhecido
  const freqId = (est.frequencies || []).find((f) => FREQUENCY_TO_CLIENT[f]);
  const frequency = FREQUENCY_TO_CLIENT[freqId] || "Semanal";

  // Valor por limpeza: ordem de preferência
  const value =
    Number(est.rate_weekly) ||
    Number(est.rate_bimonthly) ||
    Number(est.rate_monthly) ||
    Number(est.rate_other) ||
    0;

  // Checklist a partir de rooms + additional_services
  const checklist = [
    ...(est.rooms || []).map((r) => ROOM_TO_CHECKLIST_PT[r]).filter(Boolean),
    ...(est.additional_services || [])
      .map((a) => ADDITIONAL_TO_CHECKLIST_PT[a])
      .filter(Boolean),
  ];
  if (est.rooms_other) checklist.push(est.rooms_other);

  // Observations: tipo de imóvel + bedrooms/bathrooms/sqft + pets + cleaning products + key instructions
  const obsParts = [];
  if (est.home_types?.length) {
    const ht = est.home_types
      .map((id) => HOME_TYPES.find((x) => x.id === id)?.label)
      .filter(Boolean)
      .join(", ");
    obsParts.push(`Tipo de imóvel: ${ht}${est.home_type_other ? ` (${est.home_type_other})` : ""}`);
  }
  if (est.bedrooms || est.bathrooms || est.square_footage) {
    obsParts.push(
      `Quartos: ${est.bedrooms ?? "—"} · Banheiros: ${est.bathrooms ?? "—"} · ${est.square_footage ?? "—"} sqft`,
    );
  }
  if (est.pets === "yes")
    obsParts.push(`🐾 Possui pets${est.pet_names ? ` (${est.pet_names})` : ""}`);
  if (est.provides_products === "yes") obsParts.push("Cliente fornece produtos de limpeza");
  if (est.provides_products === "no") obsParts.push("Cliente NÃO fornece produtos");
  if (est.cleaning_types?.length) {
    const ct = est.cleaning_types
      .map((id) => CLEANING_TYPES.find((x) => x.id === id)?.label)
      .filter(Boolean)
      .join(", ");
    obsParts.push(`Tipo de limpeza: ${ct}`);
  }
  if (est.preferred_days?.length) {
    const pd = est.preferred_days
      .map((id) => PREFERRED_DAYS.find((x) => x.id === id)?.label)
      .filter(Boolean)
      .join(", ");
    obsParts.push(`Dias preferidos: ${pd}`);
  }
  if (est.preferred_times?.length) {
    const pt = est.preferred_times
      .map((id) => PREFERRED_TIMES.find((x) => x.id === id)?.label)
      .filter(Boolean)
      .join(", ");
    obsParts.push(`Horário: ${pt}`);
  }
  if (est.key_instructions) obsParts.push(`Acesso: ${est.key_instructions}`);
  if (est.id) obsParts.push(`— Vindo do orçamento #${est.id.slice(0, 8)}`);

  return {
    name: safeName,
    phone: est.phone || "",
    email: "",
    address,
    apt_number: "",
    door_code: "",
    city,
    state: stateCode,
    zip_code: zip,
    lat: null,
    lng: null,
    property_type: (est.home_types || []).includes("apartment") ? "Comercial" : "Residencial",
    frequency,
    value,
    observations: obsParts.join("\n"),
    photos: [],
    has_whatsapp: false,
    has_telegram: false,
    has_instagram: false,
    has_facebook: false,
    instagram_handle: "",
    facebook_handle: "",
    language: "",
    cleaning_checklist: checklist,
    auto_recurrence: true,
  };
}

function EstimateDialog({ open, onOpenChange, estimate, onSaved }) {
  const [form, setForm] = useState(estimate || EMPTY);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    setForm(estimate || EMPTY);
  }, [estimate, open]);

  function set(k, v) {
    let val = v;
    if (k === "name" || k === "prepared_by" || k === "pet_names") val = titleCase(v);
    else if (k === "email") val = lowercaseAll(v);
    setForm((f) => ({ ...f, [k]: val }));
  }

  async function save() {
    if (!form.name) {
      toast.error(t("Nome é obrigatório"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        square_footage: form.square_footage ? Number(form.square_footage) : null,
        rate_weekly: form.rate_weekly ? Number(form.rate_weekly) : null,
        rate_bimonthly: form.rate_bimonthly ? Number(form.rate_bimonthly) : null,
        rate_monthly: form.rate_monthly ? Number(form.rate_monthly) : null,
        rate_other: form.rate_other ? Number(form.rate_other) : null,
      };
      if (estimate?.id) {
        await api.patch(`/estimates/${estimate.id}`, payload);
        toast.success(t("Orçamento atualizado"));
      } else {
        await api.post("/estimates", payload);
        toast.success(t("Orçamento criado"));
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="estimate-dialog-title">
            {estimate?.id ? t("Editar orçamento") : t("Novo orçamento")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Header info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">{t("Data")}</label>
              <DatePickerUS
                testId="est-field-date"
                value={form.date || ""}
                onChange={(v) => set("date", v)}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">{t("Preparado por")}</label>
              <input
                data-testid="est-field-prepared-by"
                className="cm-input"
                value={form.prepared_by || ""}
                onChange={(e) => set("prepared_by", e.target.value)}
              />
            </div>
          </div>

          <Section title="Orçamento para">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">{t("Nome")} *</label>
                <input
                  data-testid="est-field-name"
                  className="cm-input"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder={t("João da Silva")}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">{t("Telefone")}</label>
                <input
                  data-testid="est-field-phone"
                  className="cm-input"
                  type="tel"
                  inputMode="tel"
                  maxLength={14}
                  value={form.phone || ""}
                  onChange={(e) => set("phone", formatUSPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-600">{t("Endereço")}</label>
                <input
                  data-testid="est-field-address"
                  className="cm-input"
                  value={form.address || ""}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder={t("123 Main St, Cidade, Estado 00000")}
                />
              </div>
            </div>
          </Section>

          <Section title="Pets">
            <div className="flex flex-wrap items-end gap-3">
              <YesNoGroup
                value={form.pets}
                onChange={(v) => set("pets", v)}
                testIdPrefix="est-pets"
              />
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-semibold text-slate-600">{t("Nome do(s) Pet(s)")}</label>
                <input
                  data-testid="est-field-pet-names"
                  className="cm-input"
                  value={form.pet_names || ""}
                  onChange={(e) => set("pet_names", e.target.value)}
                  disabled={form.pets !== "yes"}
                />
              </div>
            </div>
          </Section>

          <Section title="Tipo de imóvel">
            <ChoiceGrid
              items={HOME_TYPES}
              selected={form.home_types}
              onToggle={(arr) => set("home_types", arr)}
              cols={4}
              testIdPrefix="est-home"
            />
            {(form.home_types || []).includes("other") && (
              <input
                data-testid="est-field-home-type-other"
                className="cm-input mt-2"
                value={form.home_type_other || ""}
                onChange={(e) => set("home_type_other", e.target.value)}
                placeholder={t("Especifique o tipo…")}
              />
            )}
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">{t("Nº de Quartos")}</label>
                <input
                  type="number"
                  data-testid="est-field-bedrooms"
                  className="cm-input"
                  value={form.bedrooms || ""}
                  onChange={(e) => set("bedrooms", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">{t("Nº de Banheiros")}</label>
                <input
                  type="number"
                  data-testid="est-field-bathrooms"
                  className="cm-input"
                  value={form.bathrooms || ""}
                  onChange={(e) => set("bathrooms", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">{t("Metragem (sqft)")}</label>
                <input
                  type="number"
                  data-testid="est-field-sqft"
                  className="cm-input"
                  value={form.square_footage || ""}
                  onChange={(e) => set("square_footage", e.target.value)}
                />
              </div>
            </div>
          </Section>

          <Section title="Cômodos a serem limpos">
            <ChoiceGrid
              items={ROOMS}
              selected={form.rooms}
              onToggle={(arr) => set("rooms", arr)}
              cols={4}
              testIdPrefix="est-room"
            />
            {(form.rooms || []).includes("other") && (
              <input
                data-testid="est-field-rooms-other"
                className="cm-input mt-2"
                value={form.rooms_other || ""}
                onChange={(e) => set("rooms_other", e.target.value)}
                placeholder={t("Outros cômodos…")}
              />
            )}
          </Section>

          <Section title="Tipo de limpeza">
            <ChoiceGrid
              items={CLEANING_TYPES}
              selected={form.cleaning_types}
              onToggle={(arr) => set("cleaning_types", arr)}
              cols={3}
              testIdPrefix="est-cleantype"
            />
            {(form.cleaning_types || []).includes("other") && (
              <input
                data-testid="est-field-cleantype-other"
                className="cm-input mt-2"
                value={form.cleaning_type_other || ""}
                onChange={(e) => set("cleaning_type_other", e.target.value)}
                placeholder={t("Especifique…")}
              />
            )}
          </Section>

          <Section title="Frequência">
            <ChoiceGrid
              items={FREQUENCIES}
              selected={form.frequencies}
              onToggle={(arr) => set("frequencies", arr)}
              cols={4}
              testIdPrefix="est-freq"
            />
            {(form.frequencies || []).includes("other") && (
              <input
                data-testid="est-field-freq-other"
                className="cm-input mt-2"
                value={form.frequency_other || ""}
                onChange={(e) => set("frequency_other", e.target.value)}
                placeholder={t("Especifique…")}
              />
            )}
          </Section>

          <Section title="Dias preferidos">
            <ChoiceGrid
              items={PREFERRED_DAYS}
              selected={form.preferred_days}
              onToggle={(arr) => set("preferred_days", arr)}
              cols={5}
              testIdPrefix="est-day"
            />
          </Section>

          <Section title="Horário preferido">
            <ChoiceGrid
              items={PREFERRED_TIMES}
              selected={form.preferred_times}
              onToggle={(arr) => set("preferred_times", arr)}
              cols={3}
              testIdPrefix="est-time"
            />
          </Section>

          <Section title="Você fornecerá os produtos de limpeza?">
            <YesNoGroup
              value={form.provides_products}
              onChange={(v) => set("provides_products", v)}
              testIdPrefix="est-products"
            />
          </Section>

          <Section title="Serviços adicionais">
            <ChoiceGrid
              items={ADDITIONAL_SERVICES}
              selected={form.additional_services}
              onToggle={(arr) => set("additional_services", arr)}
              cols={3}
              testIdPrefix="est-extra"
            />
          </Section>

          <Section title="Tarifas (US$)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["rate_weekly", "Por limpeza semanal"],
                ["rate_bimonthly", "Por limpeza quinzenal"],
                ["rate_monthly", "Por limpeza mensal"],
                ["rate_other", "Por outra frequência"],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-slate-600">{t(lbl)}</label>
                  <div className="relative">
                    <span
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold pointer-events-none"
                      aria-hidden="true"
                    >
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid={`est-field-${k.replace("_", "-")}`}
                      className="cm-input"
                      style={{ paddingLeft: "28px" }}
                      value={form[k] || ""}
                      onChange={(e) => set(k, e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Instruções da chave / acesso">
            <textarea
              data-testid="est-field-key-instructions"
              className="cm-input min-h-[60px]"
              value={form.key_instructions || ""}
              onChange={(e) => set("key_instructions", e.target.value)}
              placeholder={t("Código da porta, alarme, onde deixar a chave…")}
            />
          </Section>

          <Section title="Situação do orçamento">
            <div className="flex gap-2 flex-wrap">
              {Object.entries(STATUS_INFO).map(([k, v]) => (
                <CheckBox
                  key={k}
                  checked={form.status === k}
                  onChange={() => set("status", k)}
                  label={v.label}
                  testId={`est-status-${k}`}
                />
              ))}
            </div>
          </Section>
        </div>

        <div className="flex flex-wrap justify-between gap-2 mt-6 sticky bottom-0 bg-white pt-3 border-t border-slate-100">
          <Button
            type="button"
            data-testid="est-convert-to-client-btn"
            onClick={() => {
              if (!form.name) {
                toast.error(t("Preencha o nome antes de enviar para o cadastro de cliente"));
                return;
              }
              const prefilled = mapEstimateToClient({ ...form, id: estimate?.id });
              toast.success(t("Dados enviados para o cadastro de cliente"));
              onOpenChange(false);
              nav("/clients", { state: { prefilled } });
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            title={t("Criar cliente já preenchido com os dados deste orçamento")}
          >
            <UserPlus size={16} className="mr-1" />
            {t("Enviar para cadastro de cliente")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="est-cancel-btn">
              {t("Cancelar")}
            </Button>
            <Button onClick={save} disabled={busy} data-testid="est-save-btn">
              {busy ? t("Salvando…") : t("Salvar orçamento")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Estimates() {
  const confirm = useConfirm();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/estimates");
      setItems(data);
    } catch (e) {
      logError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(est) {
    if (!(await confirm({
      title: t('Excluir orçamento de "{name}"?', { name: est.name }),
      destructive: true,
      confirmText: t("Excluir"),
    }))) return;
    try {
      await api.delete(`/estimates/${est.id}`);
      toast.success(t("Orçamento excluído"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function changeStatus(est, status) {
    try {
      await api.patch(`/estimates/${est.id}`, { ...est, status });
      toast.success(t("Status atualizado"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Financeiro")}
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList /> Invoice
          </h1>
          <div className="text-sm text-slate-500">
            {t("Crie e gerencie invoices/orçamentos para clientes.")}
          </div>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
          data-testid="estimates-new-btn"
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={16} className="mr-1" /> {t("Novo orçamento")}
        </Button>
      </div>

      <div className="cm-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">{t("Carregando…")}</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-slate-400">
            <ClipboardList size={40} className="mx-auto mb-3 text-slate-300" />
            {t('Nenhum orçamento ainda. Clique em "Novo orçamento" para começar.')}
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {items.map((e) => {
                const st = STATUS_INFO[e.status] || STATUS_INFO.open;
                return (
                  <div
                    key={e.id}
                    data-testid={`estimate-card-${e.id}`}
                    className="p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 truncate">{e.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {e.date ? new Date(e.date + "T00:00:00").toLocaleDateString("en-US") : "—"}
                          {e.phone ? ` · ${e.phone}` : ""}
                        </div>
                        {e.address && (
                          <div className="text-[11px] text-slate-500 truncate mt-0.5">{e.address}</div>
                        )}
                      </div>
                      <span
                        data-testid={`estimate-status-${e.id}-mobile`}
                        className={`text-[11px] inline-flex items-center px-2 py-0.5 rounded-full border whitespace-nowrap ${st.classes}`}
                      >
                        {t(st.label)}
                      </span>
                    </div>
                    <div className="flex items-center flex-wrap gap-1.5 mt-2.5">
                      {e.status === "open" && (
                        <>
                          <button
                            onClick={() => changeStatus(e, "approved")}
                            data-testid={`estimate-approve-${e.id}-mobile`}
                            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700"
                            aria-label={t("Aprovar")}
                          >
                            <CheckCircle2 size={16} />
                          </button>
                          <button
                            onClick={() => changeStatus(e, "rejected")}
                            data-testid={`estimate-reject-${e.id}-mobile`}
                            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700"
                            aria-label={t("Rejeitar")}
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      )}
                      {e.status !== "open" && (
                        <button
                          onClick={() => changeStatus(e, "open")}
                          data-testid={`estimate-reopen-${e.id}-mobile`}
                          className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600"
                          aria-label={t("Reabrir")}
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                      <button
                        onClick={async () => { await exportEstimatePDF(e, lang); toast.success(t("PDF gerado")); }}
                        data-testid={`estimate-pdf-${e.id}-mobile`}
                        className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-slate-200 bg-slate-900 text-white"
                        aria-label={t("Baixar PDF")}
                      >
                        <FileDown size={16} />
                      </button>
                      <SendMenu kind="Invoice" record={e} testIdPrefix={`estimate-send-${e.id}-mobile`} />
                      {e.status === "approved" && (
                        <button
                          onClick={() => {
                            const prefilled = mapEstimateToClient(e);
                            toast.success(t("Dados enviados para o cadastro de cliente"));
                            nav("/clients", { state: { prefilled } });
                          }}
                          data-testid={`estimate-to-client-${e.id}-mobile`}
                          className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700"
                          aria-label={t("Criar cliente")}
                        >
                          <UserPlus size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => { setEditing(e); setOpen(true); }}
                        data-testid={`estimate-edit-${e.id}-mobile`}
                        className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700"
                        aria-label={t("Editar")}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => remove(e)}
                        data-testid={`estimate-delete-${e.id}-mobile`}
                        className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700"
                        aria-label={t("Excluir")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: tabela */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">{t("Data")}</th>
                <th className="text-left p-3">{t("Cliente")}</th>
                <th className="text-left p-3">{t("Endereço")}</th>
                <th className="text-left p-3">{t("Telefone")}</th>
                <th className="text-left p-3">{t("Status")}</th>
                <th className="text-right p-3">{t("Ações")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const st = STATUS_INFO[e.status] || STATUS_INFO.open;
                return (
                  <tr
                    key={e.id}
                    data-testid={`estimate-row-${e.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="p-3 text-xs text-slate-600 whitespace-nowrap">
                      {e.date
                        ? new Date(e.date + "T00:00:00").toLocaleDateString("en-US")
                        : "—"}
                    </td>
                    <td className="p-3 font-medium">{e.name}</td>
                    <td className="p-3 text-xs text-slate-500 max-w-xs truncate">
                      {e.address || "—"}
                    </td>
                    <td className="p-3 text-xs text-slate-600">{e.phone || "—"}</td>
                    <td className="p-3">
                      <span
                        data-testid={`estimate-status-${e.id}`}
                        className={`text-xs inline-flex items-center px-2 py-0.5 rounded-full border ${st.classes}`}
                      >
                        {t(st.label)}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {e.status === "open" && (
                          <>
                            <button
                              onClick={() => changeStatus(e, "approved")}
                              data-testid={`estimate-approve-${e.id}`}
                              title={t("Marcar como aprovado")}
                              className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            >
                              <CheckCircle2 size={14} />
                            </button>
                            <button
                              onClick={() => changeStatus(e, "rejected")}
                              data-testid={`estimate-reject-${e.id}`}
                              title={t("Marcar como rejeitado")}
                              className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                        {e.status !== "open" && (
                          <button
                            onClick={() => changeStatus(e, "open")}
                            data-testid={`estimate-reopen-${e.id}`}
                            title={t("Reabrir")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                          >
                            <RotateCcw size={14} />
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            await exportEstimatePDF(e, lang);
                            toast.success(t("PDF gerado"));
                          }}
                          data-testid={`estimate-pdf-${e.id}`}
                          title={t("Baixar PDF")}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-slate-900 text-white hover:bg-slate-800"
                        >
                          <FileDown size={14} />
                        </button>
                        <SendMenu
                          kind="Invoice"
                          record={e}
                          testIdPrefix={`estimate-send-${e.id}`}
                        />
                        {e.status === "approved" && (
                          <button
                            onClick={() => {
                              const prefilled = mapEstimateToClient(e);
                              toast.success(t("Dados enviados para o cadastro de cliente"));
                              nav("/clients", { state: { prefilled } });
                            }}
                            data-testid={`estimate-to-client-${e.id}`}
                            title={t("Criar cliente com dados deste orçamento")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          >
                            <UserPlus size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditing(e);
                            setOpen(true);
                          }}
                          data-testid={`estimate-edit-${e.id}`}
                          title={t("Editar")}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => remove(e)}
                          data-testid={`estimate-delete-${e.id}`}
                          title={t("Excluir")}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
            </div>
          </>
        )}
      </div>

      <EstimateDialog
        open={open}
        onOpenChange={setOpen}
        estimate={editing}
        onSaved={load}
      />
    </AdminLayout>
  );
}
